import { useQuery } from '@tanstack/react-query';
import { productService } from '@/services/productService';
import { useOrgSettings } from './useOrgSettings';
import { useMemo } from 'react';
import { logDebug } from '@/utils/errorHandler';

export function useEffectiveAttributes(
  productId: number,
  locale?: string,
  channel?: string,
  enabled: boolean = true
) {
  const { locales, channels } = useOrgSettings();

  // Memoize the validation logic to prevent unnecessary re-renders
  const { validatedLocale, validatedChannel } = useMemo(() => {
    // Validate that the locale exists in the organization
    let validatedLocale = locale;
    if (locale && locales.length > 0) {
      const localeExists = locales.some(l => l.code === locale);
      if (!localeExists) {
        logDebug('Locale validation warning', {
          context: 'useEffectiveAttributes',
          locale: locale,
          availableLocales: locales.map(l => l.code),
          fallbackAction: 'using undefined',
          category: 'locale_validation',
        });
        validatedLocale = undefined;
      }
    }

    // Validate that the channel exists in the organization
    let validatedChannel = channel;
    if (channel && channels.length > 0) {
      const channelExists = channels.some(c => c.code === channel);
      if (!channelExists) {
        logDebug('Channel validation warning', {
          context: 'useEffectiveAttributes',
          channel: channel,
          availableChannels: channels.map(c => c.code),
          fallbackAction: 'using undefined',
          category: 'channel_validation',
        });
        validatedChannel = undefined;
      }
    }

    return { validatedLocale, validatedChannel };
  }, [locale, channel, locales, channels]);

  return useQuery({
    queryKey: ['product', productId, 'effectiveAttributes', validatedLocale, validatedChannel],
    queryFn: async () => {
      const response = await productService.getEffectiveAttributes(
        productId,
        validatedLocale,
        validatedChannel
      );
      return response;
    },
    enabled: enabled && Boolean(productId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
